import React, { useEffect, useState } from "react"
import { useSelector, useDispatch } from "react-redux"
import { AppState } from "store/store"
import store from "configureStore"
import { createRequestSave, getCompStartDate } from "utils/JsonManipulation"
import { useNavigate } from "react-router-dom"
import CommonTitle from "components/common/CommonTitle"
import CommonStepBar from "components/common/CommonStepBar"
import saveEstimation from "apis/saveEstimation"
import Layout from "components/Layout/Layout"
import { Box, FormControl, TextField, IconButton, InputAdornment, OutlinedInput, Button } from "@mui/material"
import Visibility from "@mui/icons-material/Visibility"
import VisibilityOff from "@mui/icons-material/VisibilityOff"
import LaunchIcon from "@mui/icons-material/Launch"
import {
  isEmpty,
  isNotEmailFormat,
  isNotSame,
  isNotCharacterLimit,
  isNotNumberAlphabet,
  isSameEmailId
} from "utils/Validators"
import ErrorMessage from "components/ErrorMessage"
import isHttpError from "utils/HttpErrorCheck"
import isHttpMaintenance from "utils/HttpMaintenanceCheck"
import { isErrorInSave } from "utils/JsonErrorCheck"
import RightArrowButton from "components/Button/RightArrowButton"
import GoBackButton from "components/Button/GoBackButton"
import Loading from "components/Loading"
import { persistStore } from "redux-persist"
import { planActions } from "store/actions/PlanAction"
import { qrActions } from "store/actions/QRAction"
import { userInfoActions } from "store/actions/UserInfoAction"
import { startDateCheck } from "utils/CompensationStartDateCheck"
import { APP_URL, Colors, PLANS, ErrorMessages } from "../../Const"
import styles from "./Complete.module.scss"

const limit = 128

const formatNumber = (num: string | number) => {
  const numString = typeof num === "number" ? num.toString() : num
  return numString.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
}

export interface Props {
  plan: any
}
/**
 * Step5 見積保存画面
 * Step4にて選択したプラン＋メールアドレス・パスワードを入力し、見積保存APIに連携
 * @param {any}} props - Step4で選択したplan
 */
const Complete = (props: any) => {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const mailRef = React.createRef<HTMLHeadingElement>()
  const mailConfirmRef = React.createRef<HTMLHeadingElement>()
  const passwordRef = React.createRef<HTMLHeadingElement>()
  const [mailAdress, setMailAdress] = useState("")
  const [mailConfirm, setMailConfirm] = useState("")
  const [password, setPassword] = useState("")
  const [passwordError, setPasswordError] = useState("")
  const [passwordVisible, setPasswordVisible] = useState(false)
  const [emailRejectedFlag, setEmailRejectedFlag] = useState("0")
  const [showErrors, setShowErrors] = useState(false)
  // QRState
  const { vehicleType, carModel, firstInspectionYear, firstInspectionMonth, carName, automaticBraking } = useSelector(
    (state: AppState) => state.qr
  )
  const contractorAge = useSelector((state: AppState) => state.userInfo.contractorAge)
  const youngestAge = useSelector((state: AppState) => state.userInfo.youngestAge)
  const licenceColor = useSelector((state: AppState) => state.userInfo.licenceColor)
  const purpose = useSelector((state: AppState) => state.userInfo.purpose)
  const distance = useSelector((state: AppState) => state.userInfo.distance)
  const locationName = useSelector((state: AppState) => state.userInfo.location)
  const hadAccidents = useSelector((state: AppState) => state.userInfo.hadAccidents)
  const gradeDownAccident = useSelector((state: AppState) => state.userInfo.gradeDownAccident)
  const nonFleetGrade = useSelector((state: AppState) => state.userInfo.nonFleetGrade)
  const driverScope = useSelector((state: AppState) => state.userInfo.driverScope)
  // other info
  const page = useSelector((state: AppState) => state.plan.page)
  const couponCode = useSelector((state: AppState) => state.couponCode)
  const [loading, setLoading] = useState(false)

  const scrollToMailRef = React.useCallback(() => {
    if (mailRef && mailRef.current) {
      mailRef.current.scrollIntoView({
        behavior: "smooth",
        block: "start"
      })
    }
  }, [mailRef])

  const scrollToMailConfirmRef = React.useCallback(() => {
    if (mailConfirmRef && mailConfirmRef.current) {
      mailConfirmRef.current.scrollIntoView({
        behavior: "smooth",
        block: "start"
      })
    }
  }, [mailConfirmRef])

  const scrollToPasswordRef = React.useCallback(() => {
    if (passwordRef && passwordRef.current) {
      passwordRef.current.scrollIntoView({
        behavior: "smooth",
        block: "start"
      })
    }
  }, [passwordRef])

  const scrollCheck = () => {
    if (isEmpty(mailAdress) || isNotEmailFormat(mailAdress)) scrollToMailRef()
    else if (isEmpty(mailConfirm) || isNotSame(mailAdress, mailConfirm)) scrollToMailConfirmRef()
    else if (isEmpty(password) || isNotCharacterLimit(password) || isNotNumberAlphabet(password)) scrollToPasswordRef()
  }

  const isAllowedToGoNextPage = () => {
    if (isEmpty(mailAdress) || isEmpty(mailConfirm)) {
      scrollCheck()
      return false
    }
    if (
      isEmpty(password) ||
      isNotCharacterLimit(password) ||
      isNotNumberAlphabet(password) ||
      isSameEmailId(mailAdress, password)
    ) {
      scrollCheck()
      return false
    }
    const formatError = isNotEmailFormat(mailAdress)
    const notSameMailAddress = isNotSame(mailAdress, mailConfirm)
    if (formatError || notSameMailAddress) scrollCheck()
    return !formatError && !notSameMailAddress
  }
  const UUID = useSelector((state: AppState) => state.uuid.uuid)
  const selectedPlan = (selected: number) => {
    let planName = <span />
    switch (selected.toString()) {
      case PLANS.LIGHT:
        planName = <span style={{ color: "#4eae30", fontWeight: 600 }}>ライトプラン</span>
        break
      case PLANS.STANDARD:
        planName = <span style={{ color: "#00addf", fontWeight: 600 }}>スタンダードプラン</span>
        break
      case PLANS.FULLSUPPORT:
        planName = <span style={{ color: "#b7984e", fontWeight: 600 }}>フルサポートプラン</span>
        break
      default:
        planName = <span />
    }
    return planName
  }
  // step3画面の初期表示時にstateに設定した日付
  const compensationStartDateFromState = new Date(
    useSelector((state: AppState) => state.compensationStartDate.compensationStartDate)
  )
  const onSubmit = async () => {
    // step3で設定したstateの始期日が商品改定以前かつ、現在の始期日が商品改定以降の場合、エラー
    const compensationStartDate = getCompStartDate()
    if (!startDateCheck(compensationStartDateFromState) && startDateCheck(compensationStartDate)) {
      navigate(APP_URL.ERROR, {
        state: { api: "お見積りの保存", message: ErrorMessages.VERSION_UP }
      })
      return
    }
    setLoading(true)
    setShowErrors(true)
    if (isAllowedToGoNextPage()) {
      const req = createRequestSave(
        page,
        mailAdress,
        password,
        contractorAge,
        youngestAge,
        licenceColor,
        vehicleType,
        carModel,
        firstInspectionYear,
        firstInspectionMonth,
        carName,
        automaticBraking,
        purpose,
        distance,
        locationName,
        hadAccidents,
        nonFleetGrade,
        driverScope,
        UUID,
        couponCode.couponCode,
        emailRejectedFlag,
        gradeDownAccident
      )
      console.log("REQUEST(見積保存API):", req)
      const res = await saveEstimation(req)
      console.log("RESPONSE(見積保存API):", res)

      // ストアの初期化
      dispatch(planActions.reset())
      dispatch(qrActions.reset())
      dispatch(userInfoActions.reset())

      // ローカルストレージの削除
      const persistor = persistStore(store)
      persistor.purge()

      if (isHttpMaintenance(res)) {
        // HTTP MAINTENANCE CHECK
        navigate(APP_URL.MAINTENANCE)
      } else if (isHttpError(res)) {
        // HTTP ERROR CHECK
        navigate(APP_URL.ERROR, {
          state: { api: "お見積りの保存", message: "" }
        })
      } else if (isErrorInSave(res)) {
        // Json ERROR CHECK
        navigate(APP_URL.ERROR, {
          state: { api: "お見積りの保存", message: "" }
        })
      } else {
        // 正常系
        navigate(APP_URL.FINISHED, {
          state: mailAdress
        })
      }
    }
    setLoading(false)
  }
  useEffect(() => {
    if (!page) {
      // キャッシュにプランの情報がなければ、トップページへ
      navigate(APP_URL.TOP)
    }
  }, [])
  const togglePasswordVisiblity = () => {
    if (passwordVisible) {
      setPasswordVisible(false)
    } else {
      setPasswordVisible(true)
    }
  }
  return page ? (
    <Layout>
      <Loading loadingFlag={loading} />
      <CommonStepBar num={5} />
      <CommonTitle
        titles={["お見積りの保存"]}
        read={
          <span>
            {selectedPlan(parseInt(page, 10))}のお見積りを保存します。
            <br />
            お見積り内容は、マイページからいつでも編集いただくことができ、詳細なお見積りとお申込みが可能です。
            <br />
            お客さま専用のマイページへのURLはメールで送信します。受信用のメールアドレスをご入力ください。
          </span>
        }
      />
      <Box className={styles.mailAdressWrapper}>
        <h3 ref={mailRef}>
          メールアドレス<span className={styles.required}>必須</span>
        </h3>
        <TextField
          id="outlined-basic"
          variant="outlined"
          className={styles.mailAdress}
          value={mailAdress}
          onChange={(event) => {
            setMailAdress(event.target.value)
          }}
          onBlur={(event) => setMailAdress(event.target.value)}
          inputProps={{ maxLength: limit }}
        />
        <ErrorMessage
          isEmpty={showErrors && isEmpty(mailAdress)}
          isNotEmailFormat={showErrors && !isEmpty(mailAdress) && isNotEmailFormat(mailAdress)}
        />
      </Box>
      <Box className={styles.mailAdressWrapper}>
        <h3 ref={mailConfirmRef}>
          メールアドレス(確認)<span className={styles.required}>必須</span>
        </h3>
        <TextField
          id="outlined-basic"
          variant="outlined"
          className={styles.mailAdress}
          value={mailConfirm}
          onChange={(event) => {
            setMailConfirm(event.target.value)
          }}
          onBlur={(event) => setMailConfirm(event.target.value)}
          inputProps={{ maxLength: limit }}
        />
        <ErrorMessage
          isEmpty={showErrors && isEmpty(mailConfirm)}
          isNotSameEmail={showErrors && !isEmpty(mailConfirm) && isNotSame(mailAdress, mailConfirm)}
        />
      </Box>
      <Box className={styles.passwordWrapper}>
        <h3 ref={passwordRef}>
          パスワード<span className={styles.required}>必須</span>
        </h3>
        <FormControl variant="outlined">
          <OutlinedInput
            id="password"
            placeholder=""
            type={passwordVisible ? "text" : "password"}
            value={password}
            className={styles.password}
            onChange={(event) => {
              setPassword(event.target.value)
            }}
            onBlur={(event) => setPassword(event.target.value)}
            error={!!passwordError}
            endAdornment={
              <InputAdornment position="end">
                <IconButton
                  aria-label="toggle password visibility"
                  onClick={togglePasswordVisiblity}
                  onMouseDown={togglePasswordVisiblity}
                  edge="end"
                >
                  {passwordVisible ? <Visibility /> : <VisibilityOff />}
                </IconButton>
              </InputAdornment>
            }
          />
        </FormControl>
        <p className={styles.validationNotes}>半角で英字と数字の組合せ、7文字以上、10文字以下でご入力ください。</p>
        <ErrorMessage
          isEmpty={showErrors && isEmpty(password)}
          isNotCharacterLimit={showErrors && !isEmpty(password) && isNotCharacterLimit(password)}
          isNotNumberAlphabet={
            showErrors && !isEmpty(password) && !isNotCharacterLimit(password) && isNotNumberAlphabet(password)
          }
          isSameEmailId={
            showErrors &&
            !isEmpty(password) &&
            !isNotCharacterLimit(password) &&
            !isNotNumberAlphabet(password) &&
            isSameEmailId(mailAdress, password)
          }
        />
      </Box>
      <Box>
        <h3>当社からのご案内</h3>
        <p className={styles.guidanceMessage}>
          上記のメールアドレスにおいて今後当社よりメールによるご案内をお送りしてもよろしいでしょうか？
        </p>
        <Box className={styles.toggleButtonWrap}>
          <Button
            onClick={() => {
              setEmailRejectedFlag("0")
            }}
            className={emailRejectedFlag === "0" ? styles.selected : ""}
          >
            受け取る
          </Button>
          <Button
            onClick={() => {
              setEmailRejectedFlag("1")
            }}
            className={emailRejectedFlag === "1" ? styles.selected : ""}
          >
            受け取らない
          </Button>
        </Box>
        <p className={styles.validationNotes}>
          「受け取らない」を選択した場合でも契約に関する重要なご案内は送付させていただきますのでご了承ください。
        </p>
        <p className={`${styles.validationNotes} ${styles.link}`}>
          同一のメールアドレスを当社にすでにご登録済みのお客さまは
          <a href="https://faq-ins-saison.dga.jp/car/otona/faq_detail.html?id=9002026" target="_blank" rel="noreferrer">
            「メールの受け取り設定を変更したい場合はどのようにすれば良いですか？」
            <LaunchIcon className={styles.mui_launch_guidance} />
          </a>
          より、「同一のメールアドレスで複数のご契約・マイページをお持ちのお客さま」の部分をご確認ください。
        </p>
      </Box>
      <Box className={styles.privacy}>
        <a href="https://www.sompo-direct.co.jp/company/policy/privacy/" target="_blank" rel="noreferrer">
          <span>個人情報のお取扱いについて</span>
          <div className={styles.mui_launch}>
            <LaunchIcon />
          </div>
        </a>
        <Box className={styles.nextButtonWrap}>
          <Box className={styles.buttonNext}>
            <RightArrowButton
              color={Colors.Orange}
              arrowColor={Colors.White}
              text="個人情報のお取扱いに"
              text2="同意してお見積りを保存する"
              onClick={() => {
                onSubmit()
              }}
              isSquareOnPC={false}
            />
          </Box>
        </Box>
      </Box>
      <GoBackButton where={APP_URL.PLANS} />
    </Layout>
  ) : (
    <> </>
  )
}

export default Complete
